import { useMemo, useState } from "react";
import { get } from "lodash";
import { useQuery } from "@tanstack/react-query";

import { queryClient } from "App";
import { addResizedColumns } from "./useTableConfig";
import { DIAL_SESSION_TABLE_CONSTANTS } from "routes/dial_sessions/components/DialSessionTable/DialSessionTableV3";
import UserService from "services/User";
import {
  getDialSessionTableColumns,
  getDialSessionTableFilters,
} from "routes/dial_sessions/components/DialSessionTable/DialSessionTableColumnsAndFilters";
import DialSessionService from "services/DialSessions";
import EmailUrlService from "services/EmailUrls";

export const invalidateDialSessionTableQuery = async () => {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: [DIAL_SESSION_TABLE_CONSTANTS.queryKey],
    }),
    // The Name filter's options are the dial sessions themselves.
    queryClient.invalidateQueries({ queryKey: ["dial-session-options"] }),
  ]);
};

export const handleDeleteDialSession = async id => {
  await DialSessionService.deleteSession(id);
  await invalidateDialSessionTableQuery();
};

const handleFetchDialSessionsOptions = async () => {
  const dialSessionOptions = (
    await DialSessionService.getDialSessionsForFilters()
  ).map(dialSession => ({ id: dialSession.id, name: dialSession.name }));
  return dialSessionOptions;
};

const handleFetchQuestionnairesOptions = async () => {
  const questionnaireOptions = (await EmailUrlService.getForms()).map(form => ({
    id: form.id,
    name: form.name,
  }));
  return questionnaireOptions;
};

export const useDialSessionTable = ({ tableName }) => {
  const [temporaryDisabled, setTemporaryDisabled] = useState(false);
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: () => UserService.getUserSettings(),
  });

  const { data: dialSessionOptions, isSuccess: isDialSessionOptionsFetched } =
    useQuery({
      queryKey: ["dial-session-options"],
      queryFn: handleFetchDialSessionsOptions,
    });

  const {
    data: questionnaireOptions,
    isSuccess: isQuestionnairesOptionsFetched,
  } = useQuery({
    queryKey: ["questionnaire-options"],
    queryFn: handleFetchQuestionnairesOptions,
  });

  const setTemporaryDisable = () => {
    setTemporaryDisabled(true);
    setTimeout(() => {
      setTemporaryDisabled(false);
    }, 3000);
  };

  const filters = useMemo(() => {
    if (!isDialSessionOptionsFetched || !isQuestionnairesOptionsFetched)
      return [];
    return getDialSessionTableFilters({
      dialSessionOptions,
      questionnaireOptions,
    });
  }, [
    isDialSessionOptionsFetched,
    isQuestionnairesOptionsFetched,
    dialSessionOptions,
    questionnaireOptions,
  ]);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getDialSessionTableColumns({
      temporaryDisabled,
      setTemporaryDisable,
    });
    const dialSessionTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${tableName}`,
      {}
    );

    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: dialSessionTableSettings?.resized_columns || {},
    });
    return withResizedColumns;
  }, [tableSettings, isTableSettingsFetched, tableName, temporaryDisabled]);

  return { columns, filters };
};
